import type { TFunction } from "i18next";
import { isScheduleMobileWorkingLocation } from "../../../components/Schedule/schedule-event-location.component";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import {
  filterRedundantParticipantTags,
  resolveScheduleParticipantTags
} from "./dashboard2-booking-participant-tags.utils";
import { classifyDashboard2BookingBucket } from "./dashboard2-primary-booking.utils";

const DASHBOARD2_BOOKING_TYPE_I18N_KEYS: Partial<Record<string, string>> = {
  [BookingType.WORKPLACE]: "Workplace",
  [BookingType.TEAM]: "Team",
  [BookingType.PLACEZONE]: "Workplace",
  [BookingType.OPENSPACE]: "Open Space",
  [BookingType.CONFERENCEZONE]: "Conference",
  [BookingType.PARKINGPLACE]: "Parking",
  [BookingType.VISITOR]: "Visitor",
  [BookingType.VEHICLE]: "_bookingTypeVehicle",
  [BookingType.ELECTRICCHARGINGSTATIONPLACE]: "EV charging"
};

export function resolveDashboard2BookingTypeLabel(bookingType: string, t: TFunction): string {
  const key = DASHBOARD2_BOOKING_TYPE_I18N_KEYS[bookingType];

  if (key) {
    return t(key);
  }

  return bookingType;
}

export type ResolveScheduleDisplayTypeLabelOptions = {
  resolveBookingTypeLabel?: (bookingType: string) => string;
  resolveExternalLabel?: () => string;
  resolveMobileWorkingLabel?: () => string;
};

/** Visitor / external participant role wins over the underlying booking type label. */
export function resolveScheduleDisplayTypeLabel(
  schedule: BookingScheduleInterface,
  options: ResolveScheduleDisplayTypeLabelOptions = {}
): string {
  if (options.resolveMobileWorkingLabel && isScheduleMobileWorkingLocation(schedule)) {
    return options.resolveMobileWorkingLabel();
  }

  if (classifyDashboard2BookingBucket(schedule) === "visitor") {
    return options.resolveBookingTypeLabel?.(BookingType.VISITOR) ?? BookingType.VISITOR;
  }

  const participantTags = resolveScheduleParticipantTags(schedule);
  if (participantTags.includes("external")) {
    return options.resolveExternalLabel?.() ?? "External";
  }

  if (options.resolveBookingTypeLabel) {
    return options.resolveBookingTypeLabel(schedule.bookingType);
  }

  return schedule.bookingType;
}

export function resolveScheduleDisplayTypeLabelWithTranslator(
  schedule: BookingScheduleInterface,
  t: TFunction
): string {
  return resolveScheduleDisplayTypeLabel(schedule, {
    resolveMobileWorkingLabel: () => t("_dashboard2_statusMobileWorking"),
    resolveBookingTypeLabel: bookingType => resolveDashboard2BookingTypeLabel(bookingType, t),
    resolveExternalLabel: () => t("_dashboard2_bookingParticipantTagExternal")
  });
}

export function resolveScheduleDisplayParticipantTags(
  schedule: BookingScheduleInterface,
  typeLabel: string
) {
  return filterRedundantParticipantTags(typeLabel, resolveScheduleParticipantTags(schedule));
}
